import { z } from 'zod'

const id = z.string().min(1)
const timestamp = z.iso.datetime({ offset: true }).optional()
const material = z.looseObject({ id, updatedAt: timestamp })
const keyword = z.looseObject({
  keys: z.array(z.string()),
  text: z.string(),
  title: z.string().optional(),
  id: id.optional(),
})
const place = z.looseObject({
  name: z.string(),
  note: z.string(),
  id: id.optional(),
})
const world = material.extend({
  name: z.string(),
  summary: z.string(),
  cover: z.string(),
  body: z.string(),
  keywords: z.array(keyword),
  places: z.array(place),
  visibility: z.enum(['private', 'public']),
})
const habit = z.looseObject({ id: id.optional(), text: z.string() })
const character = material.extend({
  name: z.string(),
  image: z.string(),
  avatar: z.string().optional(),
  tagline: z.string(),
  persona: z.string(),
  forbidden: z.array(z.string()).default([]),
  habits: z.array(habit).default([]),
  worldMode: z.enum(['world', 'note', 'none']),
  worldId: id.nullable(),
  worldNote: z.string(),
  album: z
    .array(
      z.looseObject({ id, image: z.string(), note: z.string().optional() })
    )
    .optional(),
})
const statusField = z.looseObject({
  id: id.optional(),
  key: z.string(),
  type: z.enum(['number', 'text', 'list']),
  initial: z.union([z.string(), z.number()]),
  rule: z.string().optional(),
  options: z.array(z.string()).optional(),
  min: z.number().nullable().optional(),
  max: z.number().nullable().optional(),
})
const status = material.extend({
  name: z.string(),
  html: z.string(),
  fields: z.array(statusField),
})
const command = material.extend({
  key: z.string(),
  title: z.string(),
  description: z.string(),
  html: z.string(),
})
const introLine = z.looseObject({
  id: id.optional(),
  who: z.enum(['char', 'user', 'narration', 'image']),
  text: z.string(),
  image: z.string().optional(),
  charId: id.optional(),
})
const start = z.looseObject({
  id: id.optional(),
  intro: z.looseObject({ lines: z.array(introLine) }),
})
const albumPlace = z.looseObject({
  scenes: z.array(
    z.looseObject({
      guide: z
        .looseObject({
          note: z.string().optional(),
          emotion: z.string().optional(),
          situation: z.string().optional(),
        })
        .nullable()
        .optional(),
    })
  ),
})
const modernWorkBody = z.looseObject({
  name: z.string(),
  characterIds: z.array(id),
  commandIds: z.array(id),
  lead: id.nullable(),
  worldId: id.nullable(),
  statusId: id.nullable(),
  starts: z.array(start),
  albumPlaces: z.array(albumPlace),
  officialCommands: z.array(z.string()),
  cover: z.string(),
  description: z.string(),
  visibility: z.enum(['public', 'private', 'link']),
})
const workBody = z
  .looseObject({
    characterIds: z.array(id).optional(),
    commandIds: z.array(id).optional(),
    worldId: id.nullable().optional(),
    statusId: id.nullable().optional(),
  })
  .superRefine((value, context) => {
    if (!Object.hasOwn(value, 'characterIds')) return
    const parsed = modernWorkBody.safeParse(value)
    if (!parsed.success) {
      for (const issue of parsed.error.issues)
        context.addIssue({
          code: 'custom',
          message: issue.message,
          path: issue.path,
        })
    }
  })
const work = z.looseObject({
  id,
  work: workBody,
  version: z.number(),
  published: z.boolean(),
  reviewStatus: z.string(),
  reviewNote: z.string().optional(),
  archived: z.boolean().optional(),
  updatedAt: timestamp,
  revision: z.string().optional(),
  internalReview: z.unknown().optional(),
  publication: z.unknown().optional(),
  audienceLocked: z.boolean().optional(),
})

export const studioCollectionSchemas = {
  world: z.looseObject({ worlds: z.array(world) }),
  character: z.looseObject({ characters: z.array(character) }),
  status: z.looseObject({ statuses: z.array(status) }),
  command: z.looseObject({ commands: z.array(command) }),
  chat: z.looseObject({ works: z.array(work) }),
} as const

export type StudioCollectionResponse = z.infer<
  (typeof studioCollectionSchemas)[keyof typeof studioCollectionSchemas]
>
export const studioUserSchema = z.looseObject({
  id,
  guest: z.boolean(),
  nickname: z.string(),
  email: z.email(),
})
